// Activities.tsx
import { ResearchActivity, ResearchActivityType, UserRole } from '@avanzu/types';
import { Box, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import PaperSection from '../../components/PaperSection';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import ContentWrapper from '../../layout/ContentWrapper';
import { apiClient } from '../../services';
import ActivityEditor from './components/ActivityEditor';
import ActivitySelector from './components/ActivitySelector';
import { LoadingComponent } from '../../components/LoadingComponent';
import HelpComponent from '../../components/HelpComponent';

export const ActivitiesPageRoles = [
  UserRole.AVANZU,
  UserRole.OWNER,
  UserRole.ADMIN,
  UserRole.MEMBER,
  UserRole.VIEWER,
];

export const Activities: React.FC = () => {
  const { currentOrganisation } = useAuth();
  const [selectedActivity, setSelectedActivity] = useState<ResearchActivity | null>(null);
  const [activities, setActivities] = useState<ResearchActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const { currentProjectId, currentProject } = useProject();
  const { enqueueSnackbar } = useSnackbar();

  // Fetch activities on component mount
  useEffect(() => {
    if (currentOrganisation?.id && currentProjectId) {
      fetchActivities();
    }
  }, [currentOrganisation?.id, currentProjectId]);

  const fetchActivities = async () => {
    if (!currentOrganisation?.id || !currentProjectId) {
      enqueueSnackbar('Organisation or plan ID not available', { variant: 'error' });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const response = await apiClient.get(
        `/organisations/${currentOrganisation.id}/projects/${currentProjectId}/activities`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch activities');
      }

      const data = await response.json();

      if (data.success) {
        setActivities(data.activities || []);
      } else {
        throw new Error(data.message || 'Failed to fetch activities');
      }
    } catch (err) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Failed to fetch activities', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleActivitySelect = (activity: ResearchActivity) => {
    setSelectedActivity(activity);
  };

  const handleCreateActivity = async () => {
    if (!currentOrganisation?.id || !currentProjectId) return;

    try {
      const newActivityData = {
        name: 'New Activity',
        description: '',
        type: ResearchActivityType.CORE,
        budgetAmount: 0,
        sortOrder: activities.length,
      };

      const response = await apiClient.post(
        `/organisations/${currentOrganisation.id}/projects/${currentProjectId}/activities`,
        newActivityData
      );

      if (!response.ok) {
        throw new Error('Failed to create activity');
      }

      const data = await response.json();

      if (data.success && data.activity) {
        const newActivity = data.activity;
        setActivities([...activities, newActivity]);
        setSelectedActivity(newActivity);
      } else {
        throw new Error(data.message || 'Failed to create activity');
      }
    } catch (err) {
      console.error('Error creating activity:', err);
      enqueueSnackbar(err instanceof Error ? err.message : 'Failed to create activity', {
        variant: 'error',
      });
    }
  };

  const handleActivityUpdate = async (updatedActivity: ResearchActivity) => {
    if (!currentOrganisation?.id || !currentProjectId) return;

    try {
      const updateData = {
        name: updatedActivity.name,
        description: updatedActivity.description,
        type: updatedActivity.type,
        budgetAmount: updatedActivity.budgetAmount,
        actualAmount: updatedActivity.actualAmount,
        isActive: updatedActivity.isActive,
        evidence: updatedActivity.evidence,
        supportDescription: updatedActivity.supportDescription,
        sortOrder: updatedActivity.sortOrder,
      };

      const response = await apiClient.put(
        `/organisations/${currentOrganisation.id}/projects/${currentProjectId}/activities/${updatedActivity.id}`,
        updateData
      );

      if (!response.ok) {
        throw new Error('Failed to update activity');
      }

      const data = await response.json();

      if (data.success && data.activity) {
        const updated = data.activity;
        setActivities((prev) =>
          prev.map((activity) => (activity.id === updated.id ? updated : activity))
        );
        setSelectedActivity(updated);
      } else {
        throw new Error(data.message || 'Failed to update activity');
      }
    } catch (err) {
      console.error('Error updating activity:', err);
      enqueueSnackbar(err instanceof Error ? err.message : 'Failed to update activity', {
        variant: 'error',
      });
    }
  };

  const handleReorderActivities = async (reorderedActivities: ResearchActivity[]) => {
    setActivities(reorderedActivities);

    try {
      const updatePromises = reorderedActivities.map(async (activity, index) => {
        const response = await apiClient.put(
          `/organisations/${currentOrganisation!.id}/projects/${currentProjectId}/activities/${activity.id}`,
          { sortOrder: index }
        );

        if (!response.ok) {
          throw new Error(`Failed to update activity ${activity.id} sort order`);
        }
        return response;
      });

      await Promise.all(updatePromises);
      const updatedActivities = reorderedActivities.map((activity, index) => ({
        ...activity,
        sortOrder: index,
      }));
      setActivities(updatedActivities);
    } catch (err) {
      console.error('Error reordering activities:', err);
      enqueueSnackbar('Failed to save activity order', { variant: 'error' });
      // Revert to original order on error
      fetchActivities();
    }
  };

  const handleActivityDelete = async (deletedActivity: ResearchActivity) => {
    if (!currentOrganisation?.id || !currentProjectId) return;

    try {
      const response = await apiClient.delete(
        `/organisations/${currentOrganisation.id}/projects/${currentProjectId}/activities/${deletedActivity.id}`
      );

      if (!response.ok) {
        throw new Error('Failed to delete activity');
      }

      setActivities((prev) => prev.filter((activity) => activity.id !== deletedActivity.id));
      if (selectedActivity?.id === deletedActivity.id) {
        setSelectedActivity(null);
      }
    } catch (err) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Failed to delete activity', {
        variant: 'error',
      });
    }
  };

  if (loading) {
    return (
      <ContentWrapper>
        <PaperSection title="R&D Activities">
          <LoadingComponent context="Loading activities..." />
        </PaperSection>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper>
      <PaperSection
        title={`R&D Activities  - ${currentProject?.name}`}
        action={
          <HelpComponent title="Define project activities.">
            <Typography variant="body2" paragraph>
              Research activities define the specific R&D work undertaken in your project. Each
              activity must have a clear research hypothesis and methodology to qualify for R&D tax
              incentives.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Core Activities:</strong> Direct experimentation and research work. These
              activities must involve systematic investigation to resolve scientific or technological
              uncertainty.
              <br />
              <strong>Supporting Activities:</strong> Activities directly related to core R&D, such
              as record keeping, project management, or feasibility studies.
            </Typography>
            <Typography variant="body2" paragraph>
              Create activities using the form on the left, then document the research hypothesis,
              methodology, and outcomes on the right. All costs (transactions, salaries, assets) are
              allocated to these activities for R&D claims.
            </Typography>
          </HelpComponent>
        }
      >
        <Box sx={{ display: 'flex', minHeight: 'calc(100vh - 200px)', gap: 2 }}>
          {/* Left Panel - Activity List */}
          <Box sx={{ width: '400px', flexShrink: 0 }}>
            <ActivitySelector
              activities={activities}
              selectedActivity={selectedActivity}
              onActivitySelect={handleActivitySelect}
              onCreateActivity={handleCreateActivity}
              onReorderActivities={handleReorderActivities}
            />
          </Box>

          {/* Right Panel - Activity Editor */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ActivityEditor
              activity={selectedActivity}
              project={currentProject}
              onActivityUpdate={handleActivityUpdate}
              onActivityDelete={handleActivityDelete}
            />
          </Box>
        </Box>
      </PaperSection>
    </ContentWrapper>
  );
};

export default Activities;
